import {
  Box,
  Card,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import { TypeColumn } from '../groupTable/TypeColumn';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import type { CashTransactionGroupType } from '@pflegenavi/shared/api';
import { formatTypeColumn } from '../groupTable/model';
import { TIME_FORMAT } from '@pflegenavi/shared/constants';

interface GroupOverviewProps {
  type: CashTransactionGroupType;
  lastUpdateDate: Date;

  cashAmount: number;
  separateFeeAmount: number;
  immediateFeeAmount: number;
  cashTransactionCount: number;

  linkedAmount: number;
  linkedSeparateFeeAmount: number;
  linkedImmediateFeeAmount: number;
  linkedTransactionCount: number;
  linkedReceiptCount: number;
  linkedPayoutCount: number;
  linkedPaymentIntentCount: number;
  linkedCashListTransactionCount: number;
  expensePartCount?: number;
}

export const GroupOverview: FC<GroupOverviewProps> = ({
  type,
  lastUpdateDate,
  cashTransactionCount,
  linkedReceiptCount,
  linkedTransactionCount,
  linkedAmount,
  linkedSeparateFeeAmount,
  linkedImmediateFeeAmount,
  linkedPaymentIntentCount,
  linkedPayoutCount,
  linkedCashListTransactionCount,
  cashAmount,
  separateFeeAmount,
  immediateFeeAmount,
  expensePartCount = 0,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const row = useMemo(() => {
    return formatTypeColumn({ type, expensePartCount }, { t });
  }, [t, type, expensePartCount]);

  const difference = cashAmount - linkedAmount;
  const separateFeeAmountDifference =
    separateFeeAmount - linkedSeparateFeeAmount;
  const immediateFeeAmountDifference =
    immediateFeeAmount - linkedImmediateFeeAmount;

  const showSeparateFeeAmount =
    separateFeeAmount !== 0 || linkedSeparateFeeAmount !== 0;
  const showImmediateFeeAmount =
    immediateFeeAmount !== 0 || linkedImmediateFeeAmount !== 0;

  const smallScreens = useMediaQuery('(max-width:895px)');

  const linkSubItem = useMemo(() => {
    if (expensePartCount > 0) {
      return (
        <SubItem
          label={t('cashManagement.group-detail.linked-expense')}
          amount={expensePartCount}
        />
      );
    }
    if (linkedPaymentIntentCount > 0) {
      return (
        <SubItem
          label={t('cashManagement.group-detail.linked-payment-intents')}
          amount={linkedPaymentIntentCount}
        />
      );
    }
    if (linkedPayoutCount > 0) {
      return (
        <SubItem
          label={t('cashManagement.group-detail.linked-payouts')}
          amount={linkedPayoutCount}
        />
      );
    }
    if (linkedCashListTransactionCount > 0) {
      return (
        <SubItem
          label={t('cashManagement.group-detail.linked-cash-list-transactions')}
          amount={linkedCashListTransactionCount}
        />
      );
    }
    return (
      <>
        <SubItem
          label={t('cashManagement.group-detail.linked-transactions')}
          amount={linkedTransactionCount}
        />
        <SubItem
          label={t('cashManagement.group-detail.linked-receipts')}
          amount={linkedReceiptCount}
        />
      </>
    );
  }, [
    expensePartCount,
    linkedCashListTransactionCount,
    linkedPaymentIntentCount,
    linkedPayoutCount,
    linkedReceiptCount,
    linkedTransactionCount,
    t,
  ]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={3}
        >
          <TypeColumn row={row} />
          <Typography fontWeight={700} fontSize={14}>
            {t('cashManagement.group-detail.last-update')}{' '}
            <Typography display={'inline'} fontSize={14}>
              {fDate(lastUpdateDate, TIME_FORMAT)}
            </Typography>
          </Typography>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          justifyContent={smallScreens ? 'center' : 'space-between'}
          flexWrap="wrap"
          gap={3}
        >
          <HeaderAmountColumn
            label={t('cashManagement.group-detail.cash-amount')}
            amount={cashAmount}
            separateFeeAmount={separateFeeAmount}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.separate-fee-amount'
            )}
            immediateFeeAmount={immediateFeeAmount}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.immediate-fee-amount'
            )}
            subItems={
              <SubItem
                label={t('cashManagement.group-detail.cash-transactions')}
                amount={cashTransactionCount}
              />
            }
          />
          <HeaderAmountColumn
            label={t('cashManagement.group-detail.linked-amount')}
            amount={linkedAmount}
            separateFeeAmount={linkedSeparateFeeAmount}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.linked-separate-fee-amount'
            )}
            immediateFeeAmount={linkedImmediateFeeAmount}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.linked-immediate-fee-amount'
            )}
            subItems={linkSubItem}
          />

          <HeaderAmountColumn
            label={t('cashManagement.group-detail.open-amount')}
            amount={difference}
            separateFeeAmount={separateFeeAmountDifference}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.open-separate-fee-amount'
            )}
            immediateFeeAmount={immediateFeeAmountDifference}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.open-immediate-fee-amount'
            )}
            subItems={null}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

interface HeaderAmountColumnProps {
  label: string;
  amount: number;
  separateFeeAmount: number;
  showSeparateFeeAmount: boolean;
  separateFeeAmountTooltipText: string;
  immediateFeeAmount: number;
  showImmediateFeeAmount: boolean;
  immediateFeeAmountTooltipText: string;
  subItems: React.ReactNode;
}

const HeaderAmountColumn: FC<HeaderAmountColumnProps> = ({
  label,
  amount,
  separateFeeAmount,
  showSeparateFeeAmount,
  separateFeeAmountTooltipText,
  immediateFeeAmount,
  showImmediateFeeAmount,
  immediateFeeAmountTooltipText,
  subItems,
}) => {
  const { fCurrencyCents } = useFormatting();
  return (
    <Stack gap={1}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="column" gap={0.5}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            minWidth: 250,
            backgroundColor: 'grey.300',
            borderRadius: 2,
            p: 1,
          }}
        >
          <Typography fontWeight={700} variant="h4">
            {fCurrencyCents(amount)}
          </Typography>
        </Box>
        {(showSeparateFeeAmount || showImmediateFeeAmount) && (
          <Stack direction="row" gap={0.5}>
            {showImmediateFeeAmount && (
              <Tooltip
                arrow
                placement="left"
                title={immediateFeeAmountTooltipText}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: '100%',
                    backgroundColor: 'grey.200',
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Typography
                    fontWeight={600}
                    variant="h5"
                    color="text.secondary"
                  >
                    {fCurrencyCents(immediateFeeAmount)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {showSeparateFeeAmount && (
              <Tooltip
                arrow
                placement="right"
                title={separateFeeAmountTooltipText}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: '100%',
                    backgroundColor: 'grey.200',
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Typography
                    fontWeight={600}
                    variant="h5"
                    color="warning.main"
                  >
                    {fCurrencyCents(separateFeeAmount)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
      {subItems}
    </Stack>
  );
};

interface SubItemProps {
  label: string;
  amount: number;
}

const SubItem: FC<SubItemProps> = ({ label, amount }) => {
  return (
    <Typography variant="subtitle2" fontSize={14}>
      {label}:{' '}
      <Typography
        display="inline"
        fontSize={14}
        variant="subtitle2"
        fontWeight={700}
      >
        {amount}
      </Typography>
    </Typography>
  );
};
